import { MAIN } from './types.js';
/**
 * Whole-schedule validation (FR03). Errors block; warnings are shown.
 * Assumes import.ts has already resolved references; unresolved ones are
 * reported again here defensively so that a schedule built in code is held to
 * the same rules as one imported from CSV.
 */
export function validateSchedule(s) {
    const out = [];
    const participants = new Map(s.participants.map((p) => [p.participantId, p]));
    const rooms = new Map(s.rooms.map((r) => [r.roomId, r]));
    const rounds = [...s.rounds].sort((a, b) => a.sequence - b.sequence);
    if (rounds.length === 0 || participants.size === 0) {
        out.push({ severity: 'error', code: 'empty_schedule', message: 'Schedule needs at least one round and one participant' });
        return out;
    }
    // Round sequence must be 1..n without gaps or duplicates.
    rounds.forEach((r, i) => {
        if (r.sequence !== i + 1) {
            out.push({ severity: 'error', code: 'invalid_sequence', roundId: r.roundId, message: `Round ${r.roundId} has sequence ${r.sequence}; expected ${i + 1}` });
        }
    });
    // Index assignments by round then participant.
    const byRound = new Map();
    for (const a of s.assignments) {
        if (!participants.has(a.participantId)) {
            out.push({ severity: 'error', code: 'unknown_participant', participantId: a.participantId, roundId: a.roundId, message: `Assignment refers to unknown participant ${a.participantId}` });
            continue;
        }
        if (a.destinationRoomId !== MAIN && !rooms.has(a.destinationRoomId)) {
            out.push({ severity: 'error', code: 'unknown_room', roomId: a.destinationRoomId, roundId: a.roundId, message: `Assignment refers to unknown room ${a.destinationRoomId}` });
            continue;
        }
        if (!rounds.some((r) => r.roundId === a.roundId)) {
            out.push({ severity: 'error', code: 'unknown_round', roundId: a.roundId, message: `Assignment refers to unknown round ${a.roundId}` });
            continue;
        }
        let m = byRound.get(a.roundId);
        if (!m)
            byRound.set(a.roundId, (m = new Map()));
        const dests = m.get(a.participantId) ?? [];
        dests.push(a.destinationRoomId);
        m.set(a.participantId, dests);
    }
    for (const round of rounds) {
        const m = byRound.get(round.roundId) ?? new Map();
        // Every participant exactly once.
        for (const p of s.participants) {
            const dests = m.get(p.participantId);
            if (!dests) {
                out.push({ severity: 'error', code: 'missing_assignment', roundId: round.roundId, participantId: p.participantId, message: `${p.participantId} has no assignment in round ${round.roundId}` });
            }
            else if (dests.length > 1) {
                const distinct = new Set(dests);
                out.push({
                    severity: 'error',
                    code: 'conflicting_assignment',
                    roundId: round.roundId,
                    participantId: p.participantId,
                    message: distinct.size > 1
                        ? `${p.participantId} is assigned to ${[...distinct].join(' and ')} in round ${round.roundId}`
                        : `${p.participantId} is assigned twice in round ${round.roundId}`,
                });
            }
        }
        // Capacity counts everyone, specialists included.
        const occupancy = new Map();
        for (const [, dests] of m) {
            const d = dests[0];
            if (d && d !== MAIN)
                occupancy.set(d, (occupancy.get(d) ?? 0) + 1);
        }
        for (const [roomId, n] of occupancy) {
            const room = rooms.get(roomId);
            if (room && n > room.capacity) {
                out.push({ severity: 'error', code: 'over_capacity', roundId: round.roundId, roomId, message: `${room.roomName} has ${n} assigned in round ${round.roundId}, capacity ${room.capacity}` });
            }
        }
    }
    // Specialists keep one room unless the row is flagged.
    for (const p of s.participants) {
        if (p.role !== 'specialist')
            continue;
        const rows = s.assignments
            .filter((a) => a.participantId === p.participantId)
            .sort((a, b) => seq(rounds, a.roundId) - seq(rounds, b.roundId));
        let home;
        for (const a of rows) {
            if (home === undefined) {
                home = a.destinationRoomId;
                continue;
            }
            if (a.destinationRoomId !== home && !a.specialistMoveFlag) {
                out.push({ severity: 'error', code: 'specialist_moved', roundId: a.roundId, participantId: p.participantId, roomId: a.destinationRoomId, message: `Specialist ${p.participantId} moves from ${home} to ${a.destinationRoomId} in round ${a.roundId} without a specialist_move flag` });
            }
            if (a.specialistMoveFlag)
                home = a.destinationRoomId;
        }
    }
    // Warn on repeat student-specialist encounters.
    const specialistsByRoundRoom = new Map();
    for (const a of s.assignments) {
        const p = participants.get(a.participantId);
        if (p?.role === 'specialist' && a.destinationRoomId !== MAIN) {
            const k = `${a.roundId}|${a.destinationRoomId}`;
            specialistsByRoundRoom.set(k, [...(specialistsByRoundRoom.get(k) ?? []), p.participantId]);
        }
    }
    const met = new Map();
    for (const round of rounds) {
        for (const a of s.assignments) {
            if (a.roundId !== round.roundId)
                continue;
            const p = participants.get(a.participantId);
            if (p?.role !== 'student' || a.destinationRoomId === MAIN)
                continue;
            for (const sp of specialistsByRoundRoom.get(`${a.roundId}|${a.destinationRoomId}`) ?? []) {
                const k = `${p.participantId}|${sp}`;
                const first = met.get(k);
                if (first) {
                    out.push({ severity: 'warning', code: 'repeat_encounter', roundId: a.roundId, participantId: p.participantId, message: `${p.participantId} meets ${sp} again in round ${a.roundId} (first in ${first})` });
                }
                else
                    met.set(k, a.roundId);
            }
        }
    }
    return out;
}
function seq(rounds, id) {
    return rounds.find((r) => r.roundId === id)?.sequence ?? Number.MAX_SAFE_INTEGER;
}
export function hasBlockingErrors(findings) {
    return findings.some((f) => f.severity === 'error');
}
//# sourceMappingURL=validate.js.map